import pino, {
  type DestinationStream,
  type Logger,
  type LoggerOptions,
} from "pino";

export const logSchemaVersion = 1;
export const logLevels = [
  "fatal",
  "error",
  "warn",
  "info",
  "debug",
  "trace",
] as const;

const forbiddenKey =
  /(?:password|passphrase|token|secret|authorization|cookie|session|magic.?link|attachment|document|payload|raw|ofx|csv|transaction|description|bank|account|routing|iban|swift|email|recipient|signature|credential|card)/i;
const safeKeys = new Set([
  "environment",
  "release",
  "service",
  "event",
  "operation",
  "outcome",
  "durationMs",
  "requestId",
  "correlationId",
  "jobId",
  "errorClass",
  "errorCode",
  "code",
  "status",
  "method",
  "dependency",
  "purpose",
  "queue",
  "template",
  "signal",
  "attempt",
  "count",
  "removed",
  "repaired",
  "expired",
  "enriched",
  "expiredFlagEvaluations",
  "ready",
]);
const identifierKeys = new Set(["requestId", "correlationId", "jobId"]);
const safeIdentifier = /^[A-Za-z0-9._:-]{1,128}$/;

export type SafeLogValue = string | number | boolean;
export type SafeLogContext = Record<string, SafeLogValue>;

function errorClass(error: unknown): string {
  if (error instanceof Error) return error.name.slice(0, 100) || "Error";
  return "UnknownError";
}

function sanitizedException(error: unknown): Readonly<{ name: string }> {
  return Object.freeze({ name: errorClass(error) });
}

function safeValue(key: string, value: unknown): SafeLogValue | undefined {
  if (identifierKeys.has(key))
    return typeof value === "string" && safeIdentifier.test(value)
      ? value
      : undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") return value.slice(0, 128);
  return undefined;
}

/**
 * Converts arbitrary library/application context to the public log allowlist.
 * Error messages and stacks are deliberately never emitted because they can
 * contain request payloads or provider data.
 */
export function sanitizeLogContext(input: unknown): SafeLogContext {
  if (!input || typeof input !== "object") return {};
  if (input instanceof Error) return { errorClass: errorClass(input) };
  const output: SafeLogContext = {};
  for (const [key, value] of Object.entries(input)) {
    if (key === "err" || key === "error") {
      output.errorClass = errorClass(value);
      continue;
    }
    if (forbiddenKey.test(key) || !safeKeys.has(key)) continue;
    const sanitized = safeValue(key, value);
    if (sanitized !== undefined) output[key] = sanitized;
  }
  return output;
}

export function createLogger(
  level = "info",
  bindings: Record<string, string> = {},
  destination?: DestinationStream,
): Logger {
  const { version, release = version, ...rest } = bindings;
  const base = sanitizeLogContext({
    schemaVersion: logSchemaVersion,
    environment: process.env.NODE_ENV ?? "development",
    release: release ?? "unknown",
    ...rest,
  });
  const options: LoggerOptions = {
    level,
    base: { schemaVersion: logSchemaVersion, ...base },
    timestamp: () => `,"timestamp":"${new Date().toISOString()}"`,
    formatters: {
      level: (label) => ({ level: label }),
    },
    hooks: {
      logMethod(args, method, numericLevel) {
        const first = args[0];
        const context =
          first && typeof first === "object" ? sanitizeLogContext(first) : {};
        return method.apply(this, [
          {
            event: "application.event",
            operation: "application",
            outcome: numericLevel >= 40 ? "failure" : "success",
            ...context,
          },
        ]);
      },
    },
  };
  return destination ? pino(options, destination) : pino(options);
}

export interface BrowserLogEvent extends SafeLogContext {
  readonly event: string;
  readonly operation: string;
  readonly outcome: string;
}

export function createBrowserLogger(
  bindings: Record<string, string> = {},
  sink: (line: string) => void = console.info,
) {
  const base = sanitizeLogContext({
    environment: bindings.environment ?? "browser",
    release: bindings.release ?? "unknown",
    service: "web",
  });
  return {
    emit(event: BrowserLogEvent): void {
      sink(
        JSON.stringify({
          timestamp: new Date().toISOString(),
          schemaVersion: logSchemaVersion,
          level: event.outcome === "failure" ? "error" : "info",
          ...base,
          ...sanitizeLogContext(event),
        }),
      );
    },
  };
}

export type ObservabilityAttribute = string | number | boolean;
export interface TraceSpan {
  setAttribute(name: string, value: ObservabilityAttribute): void;
  recordException(error: unknown): void;
  end(): void;
}
export interface TracerAdapter {
  startSpan(
    name: string,
    attributes: Record<string, ObservabilityAttribute>,
  ): TraceSpan;
}
export interface ErrorReporterAdapter {
  captureException(
    error: unknown,
    context: Record<string, ObservabilityAttribute>,
  ): void | Promise<void>;
}

const noopSpan: TraceSpan = {
  setAttribute() {},
  recordException() {},
  end() {},
};
const noopTracer: TracerAdapter = { startSpan: () => noopSpan };
const noopReporter: ErrorReporterAdapter = { captureException() {} };

export function createObservability(
  adapters: {
    tracer?: TracerAdapter;
    errorReporter?: ErrorReporterAdapter;
  } = {},
) {
  const tracer = adapters.tracer ?? noopTracer;
  const errorReporter = adapters.errorReporter ?? noopReporter;
  return {
    startRequest(method: string, _pathname: string, requestId: string) {
      const span = tracer.startSpan("http.request", {
        "http.request.method": method.toUpperCase(),
        "request.id": requestId,
      });
      return {
        setAttribute(name: string, value: ObservabilityAttribute): void {
          if (
            name === "http.response.status_code" &&
            typeof value === "number" &&
            Number.isFinite(value)
          )
            span.setAttribute(name, value);
        },
        recordException(error: unknown): void {
          span.recordException(sanitizedException(error));
        },
        end(): void {
          span.end();
        },
      } satisfies TraceSpan;
    },
    captureException(
      error: unknown,
      context: Record<string, ObservabilityAttribute>,
    ) {
      return errorReporter.captureException(
        sanitizedException(error),
        sanitizeLogContext(context),
      );
    },
  };
}
